import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { FEATURED_CASE_STUDIES, PORTFOLIO_SITES, SNAPSHOT_LABEL } from "@/data/caseStudiesData";

const ALL_STUDIES = [...FEATURED_CASE_STUDIES, ...PORTFOLIO_SITES];

const portfolioMetrics = (s) => [
  { label: "Ranking keywords", value: s.keywords },
  { label: "Est. monthly organic visits", value: s.traffic },
  { label: "Local pack keywords", value: s.localPack },
].filter((m) => m.value);

const CaseStudyProofLinks = ({ slugs = [], heading = "Proof, not promises" }) => {
  const studies = slugs.map((slug) => ALL_STUDIES.find((s) => s.slug === slug)).filter(Boolean);
  if (!studies.length) return null;

  return (
    <section className="py-16" data-testid="case-study-proof-links">
      <div className="container-custom">
        <div className="text-center mb-10">
          <h2 className="text-3xl font-bold text-white mb-3">{heading}</h2>
          <p className="text-gray-400 max-w-2xl mx-auto">
            Real client sites we built and optimized, with verifiable search visibility data.
          </p>
        </div>
        <div className={`grid gap-6 max-w-4xl mx-auto ${studies.length > 1 ? "md:grid-cols-2" : ""}`}>
          {studies.map((s) => {
            const metrics = (s.metrics || portfolioMetrics(s)).slice(0, 3);
            return (
              <Link
                key={s.slug}
                to={s.caseStudyLink || "/case-studies"}
                className="group block rounded-2xl border border-white/10 bg-white/[0.03] overflow-hidden hover:border-[#00E676]/50 transition-colors"
                data-testid={`proof-link-${s.slug}`}
              >
                {s.screenshot ? (
                  <div className="h-40 overflow-hidden border-b border-white/10">
                    <img
                      src={s.screenshot}
                      alt={`${s.domain} website built by Digital Media Ninja`}
                      className="w-full h-full object-cover object-top group-hover:scale-[1.02] transition-transform duration-300"
                      loading="lazy"
                    />
                  </div>
                ) : null}
                <div className="p-6">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-white font-bold">{s.domain}</p>
                    <span className="text-xs text-gray-500">{s.category || s.industry}</span>
                  </div>
                  {s.description ? (
                    <p className="text-gray-400 text-sm mb-4 line-clamp-2">{s.description}</p>
                  ) : null}
                  {metrics.length ? (
                    <div className="grid grid-cols-3 gap-3 mb-4">
                      {metrics.map((m) => (
                        <div key={m.label} className="text-center p-2 rounded-lg bg-white/5">
                          <p className="text-[#00E676] font-bold">{m.value}</p>
                          <p className="text-gray-500 text-[11px] leading-tight mt-1">{m.label}</p>
                        </div>
                      ))}
                    </div>
                  ) : null}
                  <span className="inline-flex items-center gap-1 text-[#00E676] text-sm font-medium">
                    See the case study <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </span>
                </div>
              </Link>
            );
          })}
        </div>
        <p className="text-center text-gray-500 text-xs mt-6">{SNAPSHOT_LABEL} · Third-party Semrush estimates, not first-party Analytics.</p>
      </div>
    </section>
  );
};

export default CaseStudyProofLinks;
